<template>
    <div class="preparation-main-page">
        <transition name="loading-screen">
            <LoadingScreen v-if="loading" />
        </transition>
        <template v-if="errors">
            <ErrorsZone :errors="errors" />
        </template>
        <template v-if="loadedOnce && !errors">
            <div class="mb-3 border-bottom">
                <div class="d-flex mb-2">
                    <h1 class="pr-3 h2 mb-0">Commandes à préparer</h1>
                    <div class="px-2">
                        <PreparationDatepicker
                            class="datepicker"
                            :value="endPreparationDate" />
                    </div>
                    <div class="pr-2 pl-1">
                        <div class="text-success mt-2"
                                v-if="endPreparationDate == todayDate">
                            <i class="fas fa-check"></i> Aujourd'hui
                        </div>
                        <button class="btn btn-warning"
                                v-if="endPreparationDate != todayDate"
                                @click="selectToday">
                            <i class="fas fa-eye"></i> Aujourd'hui
                        </button>
                    </div>
                    <div class="flex-fill px-2" id="order-search">
                        <SearchOrderForm
                            placeholder="Rechercher une commande dans Cérès (par nom, référence ou lieu)"
                        >
                        </SearchOrderForm>
                    </div>
                </div>
            </div>

            <div class="row mb-2 flex-column-reverse flex-lg-row">
                <div class="col-xl-7 col-lg-6 col-md-12 align-self-center mt-4 mt-lg-0">
                    <OrdersPreparationProgressBars
                        :pickup-points-productions-status="progressCounters['pickup_points_productions']"
                        :pickup-points-subcustomers-status="progressCounters['pickup_points_subcustomers']"
                        :short-channels-status="progressCounters['short_channels']"
                        :customer-groups-status="progressCounters['customer_groups']"
                        :stores-status="progressCounters['stores']"
                    />
                </div>
                <div class="col-xl-5 col-lg-6 col-md-12 top-right-buttons">
                    <slot name="buttons"></slot>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h3>Produits</h3>
                </div>
            </div>

            <div v-if="mainCategories && mainCategories.length > 0" class="row">
                <MainCategoryStatus
                    v-for="mainCategory in mainCategories"
                    :key="mainCategory.category.id"
                    :id="mainCategory.category.id"
                    :name="mainCategory.category.name"
                    :preparation-status="mainCategory.preparation_status"
                    :end-preparation-date="endPreparationDate"/>
            </div>
            <div v-else class="row">
                <div class="col-12">
                    <span class="nothing">Aucun produit commandé</span>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h3>Clients</h3>
                </div>
            </div>

            <div class="row">
                <div class="col-md-12">
                    <h4>Lieux de retrait</h4>
                </div>
            </div>

            <div v-if="_.values(pickupPointsAllOrders).length === 0" class="row">
                <div class="col-md-12">
                    <span class="nothing">Aucune commande client ou lieu de retrait</span>
                </div>
            </div>
            <div v-else v-for="pickupPoint in _.orderBy(_.values(pickupPointsAllOrders), ['name'])" class="row point-retrait mb-4">
                <div class="col-md-3 col-lg-2 py-2 point-retrait-nom">
                    <PickupPointProductionOrderStatus
                        :key="pickupPoint.id"
                        :end-preparation-date="endPreparationDate"
                        :pickup-point="pickupPoint"
                        :order="pickupPoint.order"
                    />
                </div>

                <div class="col-md-9 col-lg-10 py-2">
                    <div class="row">
                        <div class="col-md-12">
                            <h6><OrderIcon type="pickup_point_subcustomer" :online="true"></OrderIcon> Click & collect :</h6>
                        </div>
                    </div>
                    <div class="row">
                        <template v-if="pickupPoint.subcustomers_orders.online.length === 0">
                            <div class="col-md-12">
                                <span class="nothing">Aucune commande click & collect</span>
                            </div>
                        </template>
                        <template v-else>
                            <PickupPointCustomerOrderStatus
                                v-for="order in pickupPoint.subcustomers_orders.online"
                                :key="order.id"
                                :id="order.id"
                                :reference="order.reference"
                                :name="order.name"
                                :preparation-status="order.preparation_status"
                                :paid-at="order.paid_at"
                                :online="true"
                                :status-label="order.status_label"
                                :has-comments="order.has_comments"
                                :has-priority="order.has_priority"
                            />
                        </template>
                    </div>

                    <div class="row mt-3">
                        <div class="col-md-12">
                            <h6><OrderIcon type="pickup_point_subcustomer" :online="false"></OrderIcon> Autres commandes:</h6>
                        </div>
                    </div>
                    <div class="row">
                        <template v-if="pickupPoint.subcustomers_orders.internal.length === 0">
                            <div class="col-md-12">
                                <span class="nothing">Aucune commande</span>
                            </div>
                        </template>
                        <template v-else>
                            <PickupPointCustomerOrderStatus
                                v-for="order in pickupPoint.subcustomers_orders.internal"
                                :key="order.id"
                                :id="order.id"
                                :reference="order.reference"
                                :name="order.name"
                                :preparation-status="order.preparation_status"
                                :paid-at="order.paid_at"
                                :online="false"
                                :status-label="order.status_label"
                                :has-comments="order.has_comments"
                                :has-priority="order.has_priority"
                            />
                        </template>
                    </div>
                </div>
            </div>

            <div class="row mt-4 mb-1">
                <div class="col-md-12">
                    <h4 class="d-inline-block">Circuits courts</h4>

                    <MarkMultipleShortChannelsOrdersInvoicedModal
                        v-if="userPermissions.includes('mark_multiple_short_channels_orders_invoiced') && shortChannelsOrdersDone.length > 0"
                        :orders="shortChannelsOrdersDone"
                        class="d-inline-block ml-2 mb-1 align-middle"
                    />
                </div>
            </div>
            <div class="row">
                <div v-if="shortChannelsOrders.length === 0" class="col-md-12">
                    <span class="nothing">Aucune commande</span>
                </div>
                <StoreOrderStatus
                    v-else
                    v-for="order in _.orderBy(shortChannelsOrders, ['short_channel_name'])"
                    :key="order.short_channel_id"
                    :id="order.id"
                    :customer-id="order.short_channel_id"
                    :customer-type="order.type"
                    :end-preparation-date="endPreparationDate"
                    :reference="order.reference"
                    :name="order.short_channel_name"
                    :preparation-status="order.preparation_status"
                    :status-label="order.status_label"
                    :has-comments="order.has_comments"
                    :has-priority="order.has_priority"
                    :ready-to-prepare="order.ready_to_prepare"
                />
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h4>Groupements de clients</h4>
                </div>
            </div>
            <div class="row">
                <div v-if="customerGroupsOrders.length === 0" class="col-md-12">
                    <span class="nothing">Aucune commande</span>
                </div>
                <CustomerGroupOrderStatus
                    v-else
                    v-for="order in _.orderBy(customerGroupsOrders, ['customer_group_name'])"
                    :key="order.customer_group_id"
                    :id="order.id"
                    :customer-id="order.customer_group_id"
                    :customer-type="order.type"
                    :end-preparation-date="endPreparationDate"
                    :reference="order.reference"
                    :name="order.customer_group_name"
                    :preparation-status="order.preparation_status"
                    :status-label="order.status_label"
                    :has-comments="order.has_comments"
                    :has-priority="order.has_priority"
                />
            </div>

            <div class="row mt-4">
                <div class="col-md-12">
                    <h4>Magasins</h4>
                </div>
            </div>
            <div class="row">
                <div v-if="storesOrders.length === 0" class="col-md-12">
                    <span class="nothing">Aucune commande</span>
                </div>
                <StoreOrderStatus
                    v-else
                    v-for="order in _.orderBy(storesOrders, ['store_name'])"
                    :key="order.id || 'store' + order.store_id"
                    :id="order.id"
                    :customer-id="order.store_id"
                    :customer-type="order.type"
                    :end-preparation-date="endPreparationDate"
                    :reference="order.reference"
                    :name="order.store_name"
                    :preparation-status="order.preparation_status"
                    :status-label="order.status_label"
                    :has-comments="order.has_comments"
                    :has-priority="order.has_priority"
                />
            </div>
        </template>
    </div>
</template>

<script>
    import BootstrapDatepicker from "../../BootstrapDatepicker";
    import ErrorsZone from "../../ErrorsZone";
    import HasCommentsIcon from "../HasCommentsIcon";
    import LoadingScreen from "../../LoadingScreen";
    import MainCategoryStatus from './MainCategoryStatus';
    import MarkMultipleShortChannelsOrdersInvoicedModal from "./MarkMultipleShortChannelsOrdersInvoicedModal.vue";
    import MissingProductsIcon from "../MissingProductsIcon";
    import OrderIcon from "../OrderIcon";
    import OrdersPreparationProgressBars from "../OrdersPreparationProgressBars";
    import PickupPointCustomerOrderStatus from "./PickupPointCustomerOrderStatus";
    import PickupPointProductionOrderStatus from "./PickupPointProductionOrderStatus";
    import PreparationDatepicker from "./PreparationDatepicker";
    import SearchOrderForm from "../../SearchOrderForm"
    import StoreOrderStatus from "./StoreOrderStatus";
    import UnpaidOrderIcon from "../UnpaidOrderIcon";
    import vSelect from 'vue-select';
    import {useGetLoader} from "../../compositions/axios/loadingRequest";
    import {momentDay} from "../../../moment-with-helpers";

    export default {
        name: 'PreparationMainPage',

        components: {
            BootstrapDatepicker,
            ErrorsZone,
            HasCommentsIcon,
            LoadingScreen,
            MainCategoryStatus,
            MarkMultipleShortChannelsOrdersInvoicedModal,
            MissingProductsIcon,
            OrderIcon,
            OrdersPreparationProgressBars,
            PickupPointCustomerOrderStatus,
            PickupPointProductionOrderStatus,
            PreparationDatepicker,
            SearchOrderForm,
            StoreOrderStatus,
            UnpaidOrderIcon,
            'v-select': vSelect,
        },

        props: {
            endPreparationDate: {
                type: String,
                required: true,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loaded,
                loading,
                errors,
                load,
            };
        },

        data() {
            return {
                mainCategories: [],
                storesOrders: [],
                shortChannelsOrders: [],
                customerGroupsOrders: [],
                pickupPointsAllOrders: [],

                progressCounters: {
                    stores: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                    short_channels: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                    customer_groups: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                    pickup_points: {
                        color: '',
                        counters: {
                            numerator: 0,
                            denominator: 0,
                            nb_toremove: 0,
                        },
                    },
                },

                loadedOnce: false,
            };
        },

        created() {
            this.$echo.private(`employees`)
                .listen('OrderUpdatedEvent', (e) => {
                    if (momentDay(e.order.end_preparation_date).format('YYYY-MM-DD') === this.endPreparationDate) {
                        this.loadStateFromServer();
                    }
                });
        },

        async mounted() {
            this.loadStateFromServer();
        },

        computed: {
            formattedEndPreparationDate() {
                return momentDay(this.endPreparationDate).format('dddd Do MMMM YYYY');
            },

            todayDate() {
                return momentDay().format('YYYY-MM-DD')
            },

            shortChannelsOrdersDone() {
                return this.shortChannelsOrders.filter((order) => {
                    return order.status === 'DONE'
                })
            },
        },

        methods: {
            loadStateFromServer: async function () {
                const data = await this.load(`/espace-employes/commandes/preparation/${this.endPreparationDate}/json`);
                if (data) {
                    this.mainCategories = data.mainCategories;
                    this.storesOrders = data.storesOrders;
                    this.shortChannelsOrders = data.shortChannelsOrders;
                    this.customerGroupsOrders = data.customerGroupsOrders;
                    this.pickupPointsAllOrders = data.pickupPointsAllOrders;
                    this.progressCounters = data.progressCounters;

                    this.loadedOnce = true;
                }
            },

            selectToday() {
                return this.onSelectDate(momentDay().toDate());
            },

            onSelectDate(e) {
                if (e instanceof Date) {
                    var date = moment(e).format('YYYY-MM-DD')
                    window.location = '/espace-employes/commandes/preparation/' + date;
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .preparation-main-page {
        #order-search {
            min-width: 260px;
        }

        .top-right-buttons {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-content: center;
            align-items: center;

            @media (max-width: 350px) {
                flex-direction: column;
            }
        }

        .btn-point-retrait, .btn-order {
            border: 2px solid #252525;
            width: 100%;
            margin: 0 auto;
        }

        .point-retrait {
            @media (min-width: 768px) {
                .point-retrait-nom {
                    border-right: 3px solid #252525;
                }
            }

            .point-retrait-nom {
                .counter {
                    text-align: center;
                    font-weight: bold;
                    font-size: 1.0rem;
                }
            }
        }

        .nothing {
            font-style: italic;
            font-size: 1.3rem;
        }
    }
</style>
